import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigation } from '../data/mock';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOverLightSection, setIsOverLightSection] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine if we're on a page with light background (about page and history page are light)
  const isAboutPage = location.pathname === '/about';
  const isProjectsPage = location.pathname === '/projets';
  const isHistoryPage = location.pathname === '/histoire';

  useEffect(() => {
    const checkSectionBackground = () => {
      // About page and History page always have light background
      if (location.pathname === '/about' || location.pathname === '/histoire') {
        setIsOverLightSection(true);
        return;
      }
      
      // On landing page, check which section is under the header
      if (location.pathname === '/') {
        let foundSection = false;
        let isOverLight = false;
        
        // Check all sections to find which one is under the header
        const allSections = document.querySelectorAll('main section');
        
        for (const section of allSections) {
          const rect = section.getBoundingClientRect();
          // Header is at top (around 80px height), check if this section is under the header
          if (rect.top <= 80 && rect.bottom > 80) {
            foundSection = true;
            const bgColor = window.getComputedStyle(section).backgroundColor;
            // Light backgrounds: rgb(248, 248, 248) = #f8f8f8
            isOverLight = bgColor === 'rgb(248, 248, 248)';
            break;
          }
        }
        
        // If no section found (shouldn't happen), default to dark (Hero)
        if (!foundSection) {
          isOverLight = false;
        }
        
        setIsOverLightSection(isOverLight);
      }
    };
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      checkSectionBackground();
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Initial check with a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      checkSectionBackground();
    }, 100);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [location.pathname]);

  const handleNavigation = (href) => {
    setIsMobileMenuOpen(false);
    
    if (href.startsWith('/#')) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(href.replace('/', ''));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(href.replace('/', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      navigate(href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goHome = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Use dark text on About/History page OR when over a light section on landing page
  // Projects page has dark background, so we don't use dark text
  const usesDarkText = isAboutPage || isHistoryPage || (isOverLightSection && !isProjectsPage);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? usesDarkText 
            ? 'bg-white/70 backdrop-blur-2xl border-b border-black/5 shadow-lg shadow-black/5'
            : 'bg-[#0a0a0b]/70 backdrop-blur-2xl border-b border-white/10 shadow-lg shadow-black/20'
          : usesDarkText
            ? 'bg-white/30 backdrop-blur-sm'
            : 'bg-black/20 backdrop-blur-sm'
      }`}
    >
      {/* Animated top border accent */}
      <div 
        className={`absolute top-0 left-0 right-0 h-[1px] transition-opacity duration-500 ${
          isScrolled ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          background: 'linear-gradient(90deg, transparent 0%, #3dd3ee 20%, #c8aff0 50%, #fad24b 80%, transparent 100%)',
        }}
      />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={goHome}
            className="group flex items-center gap-3"
          >
            <div className="relative">
              <span 
                className="text-2xl font-bold tracking-tight transition-colors duration-300"
                style={{ color: usesDarkText ? '#0a0a0b' : '#ffffff' }}
              >
                Index
              </span>
              <span 
                className="text-2xl font-bold tracking-tight bg-clip-text text-transparent transition-all duration-300"
                style={{ backgroundImage: 'linear-gradient(135deg, #3dd3ee, #c8aff0)' }}
              >
                Core
              </span>
              <div 
                className="absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-500"
                style={{ backgroundImage: 'linear-gradient(90deg, #3dd3ee, #fad24b)' }}
              />
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navigation.map((item) => {
              const isCurrentPage = item.href === '/about' && location.pathname === '/about' ||
                                   item.href === '/projets' && location.pathname === '/projets' ||
                                   item.href === '/histoire' && location.pathname === '/histoire';
              const isActive = isCurrentPage;
              
              return (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.href)}
                  className="relative px-4 py-2 text-sm font-medium transition-all duration-300 group"
                  style={{ 
                    color: isActive 
                      ? (usesDarkText ? '#0a0a0b' : '#ffffff')
                      : usesDarkText 
                        ? 'rgba(10, 10, 11, 0.7)' 
                        : 'rgba(255, 255, 255, 0.7)'
                  }}
                >
                  {item.name}
                  <span 
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all duration-300 ${
                      isActive ? 'w-3/4 opacity-100' : 'w-0 group-hover:w-3/4 opacity-70'
                    }`}
                    style={{ backgroundImage: 'linear-gradient(90deg, #3dd3ee, #c8aff0)' }}
                  />
                </button>
              );
            })}
            <Button
              onClick={() => handleNavigation('/#contact')}
              className="relative ml-4 bg-gradient-to-r from-[#3dd3ee] via-[#6ee7f0] to-[#c8aff0] text-[#0a0a0b] font-semibold px-6 py-2 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(61,211,238,0.4)] overflow-hidden rounded-lg group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
              <span className="relative z-10">Contactez-nous</span>
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            style={{ color: usesDarkText ? '#0a0a0b' : '#ffffff' }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 bg-[#0a0a0b]/95 backdrop-blur-2xl border-b border-white/10 transition-all duration-500 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="flex flex-col p-6 gap-2">
          {navigation.map((item) => (
            <button
              key={item.name}
              onClick={() => handleNavigation(item.href)}
              className="text-left px-4 py-3 text-white/60 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-300 border border-transparent hover:border-white/5"
            >
              {item.name}
            </button>
          ))}
          <Button
            onClick={() => handleNavigation('/#contact')}
            className="mt-4 bg-gradient-to-r from-[#3dd3ee] via-[#6ee7f0] to-[#c8aff0] text-[#0a0a0b] font-semibold rounded-lg hover:shadow-[0_0_25px_rgba(61,211,238,0.3)]"
          >
            Contactez-nous
          </Button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
